<template>
  <Teleport to="body">
    <div class="extrato-print-root"><DocumentoImpressaoExtrato :dados="printDocument" :titulo="displayTitle" :incluir-futuros="includeFuturos" /></div>
  </Teleport>
  <div class="modal-overlay" @click.self="emitClose">
    <div class="modal-container modal-xl">
      <!-- Cabeçalho do modal -->
      <div class="modal-header nao-imprimir no-print">
        <h6>{{ displayTitle }}</h6>
        <i class="bi bi-x-circle close-icon" @click="emitClose"></i>
      </div>

      <!-- ÁREA IMPRIMÍVEL + CONTEÚDO -->
      <div class="p-3 print-area" :class="{ 'print-ready': printReady }" ref="printArea">
        <p class="print-warning">Impressão não preparada. Use o botão Imprimir e aguarde a conclusão.</p>
        <div class="extrato-content">
        <!-- Header interno do relatório (entra no PDF) -->
        <div class="relatorio-header">
          <img v-if="logoUrl" :src="logoUrl" alt="Logomarca" class="header-logo" />
        </div>

        <!-- Título e subtítulo do relatório -->
        <h3 class="relatorio-title">{{ reportTitle }}</h3>
        <div class="relatorio-subtitle">
          {{ subtitle }}
        </div>

        <template v-if="valid">
        <!-- Cabeçalho de saldos (aparece na tela e PDF) -->
        <div class="balance-context d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3">
          <div>
            <div class="fw-bold">Saldo de Referência:</div>
            <div>
              {{ formatCurrency(snapshot.saldo_atualizado) }}
              <small class="text-muted">
                (Fechamento em: {{ snapshot.saldo_atualizado_em ? formatDate(snapshot.saldo_atualizado_em) : "—" }})
              </small>
            </div>
          </div>
          <div class="text-end">
            <div class="fw-bold">Saldo Atual:</div>
            <div>{{ formatCurrency(resumo.saldo_atual) }}</div>
            <small class="text-muted">
              Posição atual em {{ formatDate(hoje) }}; independente do período selecionado.
            </small>
          </div>
        </div>

        <p class="mb-1 text-muted">Período selecionado: {{ formatDate(periodo.from) }} a {{ formatDate(periodo.to) }}</p>
        <table class="table table-sm balance-summary" aria-label="Resumo financeiro">
          <tbody><tr v-for="row in resumoLinhas" :key="row[0]">
            <th>{{ ['Saldo inicial', 'Saldo final'].includes(row[0]) ? `${row[0]} do período` : row[0] }}</th><td class="text-end">{{ formatCurrency(row[1]) }}</td>
          </tr></tbody>
        </table>
        <div v-if="referenciasLinhas.length" class="mb-3">
          <h6>Referências financeiras e diferenças de referência</h6>
          <p class="text-muted">Saldos de encerramento. As diferenças não são receitas ou despesas.</p>
          <div class="table-responsive reference-scroll" tabindex="0" role="region" aria-label="Referências financeiras">
          <table class="table table-sm reference-table">
            <thead><tr><th>Data</th><th>Referência</th><th>Calculado antes</th><th>Declarado</th><th>Diferença de referência</th></tr></thead>
            <tbody><tr v-for="row in referenciasLinhas" :key="row[1]">
              <td>{{ formatDate(row[0]) }}</td><td>{{ row[1] }}</td>
              <td>{{ formatCurrency(row[2]) }}</td><td>{{ formatCurrency(row[3]) }}</td><td>{{ formatCurrency(row[4]) }}</td>
            </tr></tbody>
          </table>
          </div>
        </div>

        </template>
        <!-- Filtros (não entram no PDF) -->
        <div class="row g-2 align-items-end nao-imprimir no-print filters-row">
          <div class="col-auto">
            <label class="form-label mb-0">De</label>
            <input type="date" class="form-control" v-model="filters.from" :disabled="busy" />
          </div>
          <div class="col-auto">
            <label class="form-label mb-0">Até</label>
            <input type="date" class="form-control" v-model="filters.to" :max="hoje" :disabled="busy" />
          </div>
          <div class="col-auto">
            <button class="btn-apl btn-primary" @click="aplicarFiltros" :disabled="busy">
              Aplicar
            </button>
            <button class="btn-lim btn-secondary ms-2" @click="resetarFiltros" :disabled="busy">
              Limpar
            </button>
          </div>

          <div class="col-12 col-xl text-end export-controls">
            <div class="right-controls d-flex flex-wrap gap-2 align-items-center justify-content-xl-end">
              <div class="form-check m-0 d-flex align-items-center me-3">
                <input id="chk-futuros" v-model="includeFuturos" :disabled="busy" type="checkbox" class="form-check-input me-2" />
                <label for="chk-futuros" class="form-check-label">Incluir lançamentos futuros</label>
              </div>

              <button class="btn btn-secondary btn-sm me-2" :disabled="busy || !valid" @click="imprimir">Imprimir</button>
              <button
                :class="['btn', isDownloading ? 'btn-success' : 'btn-danger']"
                class="btn-pdf me-2"
                :disabled="busy || !valid"
                @click="gerarPDF"
              >
                <i class="bi bi-file-earmark-pdf"></i>
                {{ isDownloading ? 'Baixando' : 'PDF' }}
              </button>

              <button
                :class="['btn', isDownloadingXls ? 'btn-success' : 'btn-success']"
                class="btn-xls"
                :disabled="busy || !valid"
                @click="gerarXLS"
              >
                <i class="bi bi-file-earmark-excel"></i>
                {{ isDownloadingXls ? 'Baixando' : 'Excel' }}
              </button>
            </div>
          </div>
        </div>

        <template v-if="valid">
        <!-- TABELA (envolta por .relatorio-body para o parser PDF/XLS) -->
        <div class="relatorio-body">
          <div class="d-flex justify-content-between align-items-center mt-3">
            <h6 class="m-0">Extrato no período</h6>

            <div class="conciliacao-legenda nao-imprimir no-print">
              <span>
                <i class="conciliacao-status-dot conciliacao-status-dot--conciliado"></i>
                Conciliado
              </span>

              <span>
                <i class="conciliacao-status-dot conciliacao-status-dot--pendente"></i>
                Não conciliado
              </span>
            </div>
          </div>
          <div class="table-responsive mt-2">
            <table class="table table-sm align-middle">
              <thead>
                <tr>
                  <th class="col-data">Data</th>
                  <th>Tipo</th>
                  <th class="text-end">Valor</th>
                  <th class="col-origem">Origem</th>
                  <th>Autorização</th>
                  <th class="text-end">Saldo do dia</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!loading && itens.length === 0">
                  <td colspan="6" class="text-center text-muted">Sem lançamentos no período.</td>
                </tr>
                <tr v-else v-for="(lin, idx) in itensVisiveis" :key="idx">
                  <td class="col-data">
                    <span
                      class="conciliacao-status-dot"
                      :class="lin.conciliado
                        ? 'conciliacao-status-dot--conciliado'
                        : 'conciliacao-status-dot--pendente'"
                      :title="lin.conciliado
                        ? 'Lançamento conciliado'
                        : 'Lançamento ainda não conciliado'"
                    ></span>

                    {{ formatDate(lin.dt_caixa) }}
                  </td>
                  <td>
                    <span :class="lin.tipo === 'RECEITA' ? 'text-success' : 'text-danger'">
                      {{ displayTipo(lin) }}
                    </span>
                  </td>
                  <td class="text-end">{{ formatCurrency(lin.valor) }}</td>
                  <td class="col-origem">
                    <span
                      class="origem-texto"
                      :title="lin.origem_label || lin.origem_tabela"
                    >
                      {{ lin.origem_label || lin.origem_tabela }}
                    </span>
                  </td>
                  <td>
                    <span>{{ lin.numero_autorizacao || '—' }}</span>
                  </td>
                    <td class="text-end">
                      <span v-if="isFirstOfDay(itensVisiveis, idx)">
                        {{ formatCurrency(eodPeriodoMapFull[dateKey(lin.dt_caixa)]) }}
                      </span>
                    </td>
                </tr>
              </tbody>
                <tfoot v-if="!loading">
                    <tr class="fw-bold">
                        <td colspan="2">Total de lançamentos no período</td>
                        <td class="text-end">
                        {{ formatCurrency(totaisPeriodo.saldo) }}
                        </td>
                        <td colspan="3">
                        <small class="text-muted">
                            Receitas: {{ formatCurrency(totaisPeriodo.rec) }} /
                            Despesas: {{ formatCurrency(totaisPeriodo.desAbs) }}
                        </small>
                        </td>
                    </tr>
                </tfoot>
            </table>
          </div>
        </div>

        <!-- Lançamentos Futuros -->
        <div class="relatorio-body" v-if="!loading && includeFuturos && futuros.length">
          <h6 class="mt-4">Lançamentos Futuros</h6>
          <p>Projeção apenas dos lançamentos exibidos, sem aplicar referências futuras.</p>
          <div class="table-responsive mt-2">
            <table class="table table-sm align-middle">
              <thead>
                <tr>
                  <th>Data (venc.)</th>
                  <th>Tipo</th>
                  <th class="text-end">Valor</th>
                  <th>Origem</th>
                  <th>Autorização</th>
                  <th class="text-end">Saldo previsto</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(lin, idx) in futuros" :key="'fut-'+idx">
                  <td>{{ formatDate(lin.dt_caixa) }}</td>
                  <td>
                    <span :class="lin.tipo === 'RECEITA' ? 'text-success' : 'text-danger'">
                      {{ displayTipo(lin) }}
                    </span>
                  </td>
                  <td class="text-end">{{ formatCurrency(lin.valor) }}</td>
                  <td><span>{{ lin.origem_label || lin.origem_tabela }}</span></td>
                  <td>{{ lin.numero_autorizacao || '—' }}</td>
                    <td class="text-end">
                      <span v-if="isFirstOfDay(futuros, idx)">
                        {{ formatCurrency(eodFuturosMapFull[dateKey(lin.dt_caixa)]) }}
                      </span>
                    </td>
                </tr>
              </tbody>
              <tfoot>
                <tr class="fw-bold">
                  <td colspan="2">Lançamentos Futuros</td>
                  <td class="text-end">
                    {{ formatCurrency(totaisFuturos.saldo) }}
                  </td>
                  <td colspan="2">
                    <small class="text-muted">
                      Receitas: {{ formatCurrency(totaisFuturos.rec) }} /
                      Despesas: {{ formatCurrency(totaisFuturos.desAbs) }}
                    </small>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        </template>
        <div v-if="valid" class="d-flex flex-wrap gap-2 justify-content-between align-items-center mt-3 nao-imprimir no-print">
          <div>Mostrando {{ startRow }} a {{ endRow }} de {{ paginacao.total }} registros</div>
          <BFormSelect :model-value="pageSize" :options="pageOptions.map(n => ({ value: n, text: n + ' / página' }))"
            size="sm" style="width: 6rem" :disabled="busy" @update:model-value="changeLimit" />
          <div>
            <BPagination :model-value="page" :per-page="pageSize" :total-rows="paginacao.total"
              size="sm" :limit="5" :disabled="busy" @update:model-value="mudarPagina" />
            <span class="small">Página {{ page }} de {{ totalPages }}</span>
          </div>
        </div>

        <!-- Loading / Erro -->
        <div v-if="loading" class="text-center text-muted py-3">
          Carregando extrato…
        </div>
        <div v-if="error" class="alert alert-danger mt-2 nao-imprimir no-print">
          {{ error }}
          <button class="btn btn-sm btn-outline-danger ms-2" :disabled="busy" @click="carregar()">Tentar novamente</button>
        </div>
        <div v-if="notice" class="alert alert-info no-print">{{ notice }}</div>

        <!-- Rodapé (entra no PDF) -->
        <div class="relatorio-footer">
          <p class="text-left">
            Impresso em {{ formatarDataAtual() }}, por {{ userName }}
          </p>
        </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DocumentoImpressaoExtrato from './DocumentoImpressaoExtrato.vue';
import { BPagination, BFormSelect } from 'bootstrap-vue-next';
import axios from "@/services/axios";
import { ref, reactive, computed, onMounted, onBeforeUnmount, nextTick, watch, getCurrentInstance } from "vue";
import { imageToPdfPng } from '@/utils/pdfImages';
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
// import utils from "@/utils/globalUtils.js";

import { mapaSaldos, totaisResumo, linhasReferencias, linhasResumo } from '@/utils/extratoPresentation.mjs';

export default {
  name: "ModalExtratoConta",
  components: { BPagination, BFormSelect, DocumentoImpressaoExtrato },
  props: {
    contaId: { type: [Number, String], required: true },
    defaultFrom: { type: String, default: null }, // YYYY-MM-DD
    defaultTo:   { type: String, default: null }
  },
  setup(props, { emit }) {

    const hoje = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());

    // estado
    const loading = ref(false);
    const error   = ref("");
    const page    = ref(1);
    const pageSize= ref(10);
    const pageOptions = [10, 15, 20, 25, 50];
    const valid = ref(false), notice = ref('');
    const isPrinting = ref(false), printReady = ref(false), printItems = ref([]);
    const busy = computed(() => loading.value || isDownloading.value || isDownloadingXls.value || isPrinting.value);
    let active = null;

    const isDownloading = ref(false);
    const isDownloadingXls = ref(false);
    const printArea = ref(null);
    const printDocument = ref(null);

    // globais (logo/usuário)
    const { appContext } = getCurrentInstance() || {};
    const globalState = appContext?.config?.globalProperties?.$globalState || {};
    const userName = globalState?.userName || "";
    const logoPath = globalState?.logoPath || "";

    const snapshot = reactive({
      saldo_atualizado: null,
      saldo_atualizado_em: null
    });
    const periodo = reactive({ from: null, to: null });
    const resumo  = reactive({ total_receitas: 0, total_despesas: 0, saldo_atual: null, saldo_fim_periodo: null, saldo_inicio_periodo: null });
    const paginacao = reactive({ total: 0, page: 1, pageSize: 10 });
    const itens = ref([]);
    const itensVisiveis = computed(() => printReady.value ? printItems.value : itens.value);
    const startRow = computed(() => paginacao.total ? (page.value - 1) * pageSize.value + 1 : 0);
    const endRow = computed(() => Math.min(page.value * pageSize.value, paginacao.total));

    const includeFuturos = ref(false);

    const eodPeriodoMapFull = ref({});
    const eodFuturosMapFull = ref({});
    const referenciasLinhas = ref([]);
    const resumoLinhas = ref([]);
    const futurosResumo = ref({});
    function aplicarCalculo(data) {
      eodPeriodoMapFull.value = mapaSaldos(data.saldos_diarios);
      eodFuturosMapFull.value = mapaSaldos(data.futuros_saldos_diarios);
      referenciasLinhas.value = linhasReferencias(data.referencias_periodo);
      resumoLinhas.value = linhasResumo(data.resumo);
      futurosResumo.value = data.futuros_resumo || {};
      Object.assign(resumo, data.resumo, {
        saldo_fim_periodo: data.resumo.saldo_ao_fim_periodo
      });
    }

    const filters = reactive({
      from: props.defaultFrom || null,
      to:   props.defaultTo   || hoje
    });

    // Constrói URL absoluta a partir de $globalState.logoPath (funciona p/ "api/...", "/api/..." e absolutos)
    const logoUrl = computed(() => {
      const raw = (globalState?.logoPath || "").trim();
      if (!raw) return "";
      try {
        // new URL com base no origin garante absoluto correto: http(s)://hipica.local/...
        return new URL(raw, window.location.origin).href;
      } catch {
        return "";
      }
    });

    const totaisPeriodo = computed(() => totaisResumo(resumo));

    const totalPages = computed(() => {
      if (!paginacao.total) return 1;
      return Math.max(1, Math.ceil(paginacao.total / paginacao.pageSize));
    });

    const futuros = ref([]);
    const totaisFuturos = computed(() => totaisResumo(futurosResumo.value));

    // Converte valor para número seguro
    // Parser robusto para "290,49", "290.49", "1.234,56", "1,234.56", etc.
    function toNumber(v) {
    if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
    const s0 = String(v ?? '').trim();
    if (!s0) return 0;
    // mantém só dígitos, ponto, vírgula e sinal
    const s = s0.replace(/[^\d.,-]/g, '');

    const hasComma = s.includes(',');
    const hasDot   = s.includes('.');

    // Se tem vírgula e ponto: o último símbolo é o decimal; o outro é milhar.
    if (hasComma && hasDot) {
        const lastComma = s.lastIndexOf(',');
        const lastDot   = s.lastIndexOf('.');
        const decimalSep = lastComma > lastDot ? ',' : '.';
        const thouSep    = decimalSep === ',' ? '.' : ',';
        const normalized = s.replace(new RegExp('\\' + thouSep, 'g'), '').replace(decimalSep, '.');
        return parseFloat(normalized) || 0;
    }

    // Só vírgula => vírgula é decimal
    if (hasComma) return parseFloat(s.replace(/\./g, '').replace(',', '.')) || 0;

    // Só ponto (ou nenhum) => ponto é decimal (ou número inteiro)
    return parseFloat(s) || 0;
    }

    // One read-only snapshot for all export/print rows; no cross-page mixing.
    async function fetchAllExtratoForExport() {
      if (!valid.value || !active) throw new Error('Carregue um extrato válido antes de exportar.');
      const generation = carga, account = props.contaId;
      try {
        const { data } = await axios.get(`/financeiro/contas-financeiras/${account}/extrato`, {
          params: { ...active, page: 1, pageSize: pageSize.value, completo: '1' }
        });
        if (generation !== carga || account !== props.contaId) throw new Error('A conta ou consulta mudou. Tente novamente.');
        if (data.calculo.revisao !== active.revisao || data.itens.length !== data.paginacao.total ||
          data.periodo.from !== active.from || data.periodo.to !== active.to) throw new Error('Extrato completo incompatível. Atualize e tente novamente.');
        aplicarCalculo(data);
        Object.assign(snapshot, data.snapshot);
        contaApelido.value = data.conta?.apelido || '';
        contaInstituicao.value = data.conta?.instituicao || '';
        futuros.value = data.futuros || [];
        return { all: data.itens, total: data.paginacao.total, fut: data.futuros || [], data };
      } catch (e) {
        if (generation === carga) {
          invalidar();
          if (e?.response?.status === 409) {
            active = { ...active, revisao: undefined };
            await carregar();
            throw new Error('O extrato foi atualizado. Confira os novos valores e tente novamente.');
          }
        }
        throw e;
      }
    }

    function dateKey(d) {
      if (!d) return '';
      const s = typeof d === 'string' ? d : (d?.toString?.() || '');
      return s.slice(0, 10); // YYYY-MM-DD
    }

    // último lançamento do dia?
    function isLastOfDay(list, idx) {
      if (!Array.isArray(list) || idx == null || idx < 0 || idx >= list.length) return false;

      const curDt  = list[idx]?.dt_caixa;
      const nextDt = list[idx + 1]?.dt_caixa;

      if (!curDt) return false;

      const cur = dateKey(curDt);
      const nxt = nextDt ? dateKey(nextDt) : null;

      return !nxt || nxt !== cur;
    }

    // primeiro lançamento do dia?
    function isFirstOfDay(list, idx) {
      if (!Array.isArray(list) || idx == null || idx < 0 || idx >= list.length) return false;

      const curDt  = list[idx]?.dt_caixa;
      const prevDt = list[idx - 1]?.dt_caixa;

      if (!curDt) return false;

      const cur = dateKey(curDt);
      const prv = prevDt ? dateKey(prevDt) : null;

      return !prv || prv !== cur;
    }    

    // Monta linhas para PDF (com moeda já formatada)
    function buildRowsForPdfWithEod(items, eodMap) {
      const rows = [];
      for (let i = 0; i < items.length; i++) {
        const l = items[i];
        const day = dateKey(l.dt_caixa);
        rows.push([
          formatDate(l.dt_caixa),
          displayTipo(l),
          new Intl.NumberFormat('pt-BR', { style:'currency', currency:'BRL' }).format(toNumber(l.valor)),
          (l.origem_label || l.origem_tabela || ''),
          (l.numero_autorizacao || ''),
          isFirstOfDay(items, i)
            ? formatCurrency(eodMap[day])
            : ''
        ]);
      }
      return rows;
    }

    const contaApelido = ref('');
    const contaInstituicao = ref('');

    const displayTitle = computed(() => {
    // Se tiver apelido e/ou instituição, mostra "Extrato da Conta - Apelido (Instituição)"
    if (contaApelido.value || contaInstituicao.value) {
        const apel = contaApelido.value || `Conta ${props.contaId}`;
        const inst = contaInstituicao.value ? ` (${contaInstituicao.value})` : '';
        return `Extrato da Conta - ${apel}${inst}`;
    }
    // Fallback
    return `Extrato da Conta #${props.contaId}`;
    });

    // Título do PDF/print também passa a usar a instituição
    const reportTitle = displayTitle;
    const subtitle = computed(() => {
      const pfrom = periodo.from ? formatDate(periodo.from) : "—";
      const pto   = formatDate(periodo.to || hoje);
      const sref  = formatCurrency(snapshot.saldo_atualizado);
      const sat   = formatCurrency(resumo.saldo_atual);
      return `Período: ${pfrom} a ${pto} · Saldo Ref.: ${sref} · Saldo Atual: ${sat}`;
    });

    function formatCurrency(n) {
      if (n == null) return 'Desconhecido';
      const v = typeof n === "number" ? n : parseFloat(n || 0);
      return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" })
        .format(isNaN(v) ? 0 : v);
    }
    function formatDate(d) {
      if (!d) return "—";
      if (typeof d === "string") {
        const s = d.trim();
        // pega somente a parte YYYY-MM-DD, ignorando 'T...Z' se houver
        const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (m) return `${m[3]}/${m[2]}/${m[1]}`;
      }
      // fallback: Date object / timestamp numérico'
      const dt = new Date(d);
      if (isNaN(dt.getTime())) return "—";
      const pad2 = (n) => String(n).padStart(2, "0");
      return `${pad2(dt.getDate())}/${pad2(dt.getMonth() + 1)}/${dt.getFullYear()}`;
    }
    function formatarDataAtual() {
      return new Date().toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });
    }

    function cleanFpName(s) {
      return (s || '')
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '') // sem acentos
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();
    }

    function displayTipo(l) {
      const t  = String(l?.tipo || '').toUpperCase();
      const fpRaw = (l?.forma_pagamento_nome || '').trim();
      const fp = cleanFpName(fpRaw);
      // se for "A informar" (qualquer variação de espaços/caixa), não mostra
      return fp && fp !== 'a informar' ? `${t} — ${fpRaw}` : t;
    }

    let carga = 0;
    function invalidar() {
      valid.value = false;
      printReady.value = false;
      printItems.value = [];
      itens.value = []; futuros.value = [];
      referenciasLinhas.value = []; resumoLinhas.value = [];
      eodPeriodoMapFull.value = {}; eodFuturosMapFull.value = {};
      futurosResumo.value = {};
      for (const key of Object.keys(resumo)) resumo[key] = null;
      snapshot.saldo_atualizado = null; snapshot.saldo_atualizado_em = null;
      paginacao.total = 0; page.value = 1;
    }
    function receber(data) {
      aplicarCalculo(data);
      Object.assign(snapshot, data.snapshot);
      Object.assign(periodo, data.periodo);
      Object.assign(paginacao, data.paginacao);
      page.value = data.paginacao.page;
      itens.value = data.itens; futuros.value = data.futuros || [];
      contaApelido.value = data.conta?.apelido || '';
      contaInstituicao.value = data.conta?.instituicao || '';
      active = { ...data.periodo, revisao: data.calculo.revisao };
      valid.value = true;
    }
    async function carregar(targetPage = 1) {
      const request = ++carga, account = props.contaId;
      const fixed = active ? { ...active } : { ...(filters.from ? { from: filters.from } : {}), ...(filters.to ? { to: filters.to } : {}) };
      invalidar(); loading.value = true; error.value = ''; notice.value = '';
      try {
        for (let attempt = 0; attempt < 2; attempt++) {
          try {
            const { data } = await axios.get(`/financeiro/contas-financeiras/${account}/extrato`, {
              params: { ...fixed, page: targetPage, pageSize: pageSize.value }
            });
            if (request !== carga || account !== props.contaId) return;
            receber(data);
            return;
          } catch (e) {
            if (request !== carga) return;
            if (e?.response?.status !== 409 || attempt) throw e;
            delete fixed.revisao; targetPage = 1;
            notice.value = 'Os dados financeiros mudaram. Extrato atualizado na página 1, mantendo o período.';
          }
        }
      } catch (e) {
        if (request !== carga) return;
        invalidar();
        error.value = e?.response?.data?.error || e.message || 'Falha ao carregar extrato.';
      } finally {
        if (request === carga) loading.value = false;
      }
    }
    function aplicarFiltros() {
      if (busy.value) return;
      active = null;
      return carregar();
    }
    function resetarFiltros() {
      filters.from = null; filters.to = hoje;
      return aplicarFiltros();
    }
    function changeLimit(value) {
      if (busy.value || !pageOptions.includes(Number(value))) return;
      pageSize.value = Number(value);
      return carregar();
    }
    function mudarPagina(p) {
      if (busy.value || p < 1 || p > totalPages.value) return;
      return carregar(p);
    }
    function emitClose(){ emit('close') }
    function terminarImpressao() {
      printReady.value = false; printItems.value = []; printDocument.value = null; isPrinting.value = false;
    }
    async function imprimir() {
      if (busy.value || !valid.value) return;
      isPrinting.value = true; error.value = '';
      try {
        const result = await fetchAllExtratoForExport();
        printItems.value = result.all; printReady.value = true;
        printDocument.value = { ...result.data, itens: result.all };
        await nextTick();
        window.print();
      } catch (e) {
        error.value = e?.response?.data?.error || e.message || 'Falha ao preparar impressão.';
      } finally { terminarImpressao(); }
    }
    function printShortcut(event) {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'p') {
        event.preventDefault(); imprimir();
      }
    }

    // === PDF (mesmo padrão dos relatórios) ===
    async function gerarPDF() {
      if (busy.value || !valid.value) return;
      error.value = '';
      isDownloading.value = true;
      const generation = carga;
      try {
        // Busca todos os registros do período (não só a página visível) + futuros
        const { all, fut, data: exportData } = await fetchAllExtratoForExport();

        const pdf = new jsPDF('p', 'mm', 'a4');
        const marginLeft = 15, marginRight = 15, marginBottom = 20;
        const topMargin  = 15;
        let cursorY = topMargin;

        const pageWidth  = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();

        // Logo (URL absoluta via logoUrl) com tolerância a erro/timeout
        if (logoUrl.value) {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.src = logoUrl.value;
          await new Promise((resolve) => {
            img.onload = resolve;
            img.onerror = resolve;       // não trava se 404
            setTimeout(resolve, 1500);   // timeout de segurança
          });
          if (img.naturalWidth) {
            const logoPng = imageToPdfPng(img);
            if (logoPng) pdf.addImage(logoPng, 'PNG', marginLeft, cursorY, 25, 12.5);
          }
        }

        // Título e subtítulo
        pdf.setFontSize(14);
        pdf.setFont(undefined, 'bold');
        pdf.text(reportTitle.value, pageWidth / 2, cursorY + 10, { align: 'center' });
        cursorY += 20;

        if (subtitle.value) {
          pdf.setFontSize(12);
          pdf.setFont(undefined, 'normal');
          pdf.text(subtitle.value, pageWidth / 2, cursorY, { align: 'center' });
          cursorY += 10;
        } else {
          cursorY += 5;
        }

        // --- Título da 1ª tabela (espaço reduzido) ---
        pdf.setFontSize(12);
        pdf.setFont(undefined, 'bold');
        pdf.text('Extrato no Período', marginLeft, cursorY);
        cursorY += 4; // (antes era 6)

        // Tabela do período
        const headers = ['Data', 'Tipo', 'Valor', 'Origem', 'Autorização', 'Saldo do dia'];
        const eodPeriodo = mapaSaldos(exportData.saldos_diarios);
        const bodyRows = buildRowsForPdfWithEod(all, eodPeriodo);

        autoTable(pdf, {
          startY: cursorY,
          head: [headers],
          body: bodyRows,
          margin: { left: marginLeft, right: marginRight, bottom: marginBottom },
          styles: { fontSize: 7, cellPadding: 1, overflow: 'linebreak' },
          headStyles: { fillColor: [200, 200, 200], textColor: [50, 50, 50], fontSize: 7, fontStyle: 'bold' },
          theme: 'grid'
        });

        // Avança cursor após a tabela
        cursorY = pdf.lastAutoTable.finalY + 3;

        // Totais do período
        const fBRL = formatCurrency;
        const totals = totaisResumo(exportData.resumo);
        const sumReceitas = totals.rec, sumDespesasAbs = totals.desAbs, saldoPeriodo = totals.saldo;
        const closingMargin = { top: topMargin, left: marginLeft, right: marginRight, bottom: marginBottom };
        const summaryOptions = {
          head: [['Resumo financeiro', 'Valor']],
          body: linhasResumo(exportData.resumo).map(([label, value]) => [label, fBRL(value)]),
          margin: closingMargin, styles: { fontSize: 8, cellPadding: 1 },
          columnStyles: { 1: { halign: 'right' } }, pageBreak: 'avoid', rowPageBreak: 'avoid'
        };
        const referenceOptions = {
          head: [['Data', 'Referência', 'Calculado antes', 'Declarado', 'Diferença de referência']],
          body: linhasReferencias(exportData.referencias_periodo).map(r => [formatDate(r[0]), r[1], ...r.slice(2).map(fBRL)]),
          margin: closingMargin, styles: { fontSize: 7, cellPadding: 1 },
          columnStyles: { 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right' } },
          pageBreak: 'avoid', rowPageBreak: 'avoid'
        };
        const closingTables = (doc, startY) => {
          autoTable(doc, { ...summaryOptions, startY });
          let nextY = doc.lastAutoTable.finalY + 2;
          if (referenceOptions.body.length) {
            autoTable(doc, { ...referenceOptions, startY: nextY });
            nextY = doc.lastAutoTable.finalY + 2;
          }
          return nextY;
        };
        const measure = new jsPDF();
        const closingHeight = closingTables(measure, topMargin) - topMargin + 8;
        // Keep a closing block that fits on one page together, including its totals.
        if (measure.getNumberOfPages() === 1 && cursorY + closingHeight > pageHeight - marginBottom) {
          pdf.addPage();
          cursorY = topMargin;
        }
        cursorY = closingTables(pdf, cursorY);

        // Espaço para "Total: ..." (6mm)
        if (cursorY + 6 > pageHeight - marginBottom) {
          pdf.addPage();
          cursorY = topMargin;
        }
        pdf.setFontSize(7);
        pdf.setFont(undefined, 'bold');
        pdf.text(`Lançamentos no período: ${fBRL(saldoPeriodo)}`, marginLeft, cursorY);
        cursorY += 4;

        // Espaço para "Receitas / Despesas" (4mm)
        if (cursorY + 4 > pageHeight - marginBottom) {
          pdf.addPage();
          cursorY = topMargin;
        }
        pdf.setFont(undefined, 'normal');
        pdf.text(`Receitas: ${fBRL(sumReceitas)} / Despesas: ${fBRL(sumDespesasAbs)}`, marginLeft, cursorY);
        cursorY += 2;

        // ===== Seção: Lançamentos Futuros =====
        const futEnabled = includeFuturos.value && fut && fut.length;

        if (futEnabled) {
          // Respiro antes da seção Futuros e checagem de espaço
          cursorY += 12; // espaço maior antes do título
          const spaceNeededForFuture = 18; // título + gap + header
          if (cursorY + spaceNeededForFuture > pageHeight - marginBottom) {
            pdf.addPage();
            cursorY = topMargin;
          }

          // Título
          pdf.setFontSize(12);
          pdf.setFont(undefined, 'bold');
          pdf.text('Projeção dos lançamentos exibidos (sem referências futuras)', marginLeft, cursorY);
          cursorY += 4;

          // Tabela
          const futRows = buildRowsForPdfWithEod(fut, mapaSaldos(exportData.futuros_saldos_diarios));
          const startYFuture = cursorY + 1;

          autoTable(pdf, {
            startY: startYFuture,
            head: [['Data', 'Tipo', 'Valor', 'Origem', 'Autorização', 'Saldo previsto']],
            body: futRows,
            margin: { left: marginLeft, right: marginRight, bottom: marginBottom },
            styles: { fontSize: 7, cellPadding: 1, overflow: 'linebreak' },
            headStyles: { fillColor: [220, 220, 220], textColor: [40, 40, 40], fontSize: 7, fontStyle: 'bold' },
            theme: 'grid'
          });

          cursorY = pdf.lastAutoTable.finalY + 4;

          // Totais dos futuros
          if (cursorY + 10 > pageHeight - marginBottom) {
            pdf.addPage();
            cursorY = topMargin;
          }

          const futureTotals = totaisResumo(exportData.futuros_resumo);
          const fRec = futureTotals.rec, fDesAbs = futureTotals.desAbs, fSaldo = futureTotals.saldo;

          pdf.setFontSize(7);
          pdf.setFont(undefined, 'bold');
          pdf.text(`Total Futuros: ${fBRL(fSaldo)}`, marginLeft, cursorY);
          cursorY += 4;

          pdf.setFont(undefined, 'normal');
          pdf.text(`Receitas: ${fBRL(fRec)} / Despesas: ${fBRL(fDesAbs)}`, marginLeft, cursorY);
          cursorY += 2;
        }

        // Rodapé em todas as páginas
        const pageCount  = pdf.getNumberOfPages();
        pdf.setFontSize(7);
        pdf.setFont(undefined, 'italic');
        for (let i = 1; i <= pageCount; i++) {
          pdf.setPage(i);
          const footerY = pageHeight - marginBottom / 2;
          pdf.text(`Impresso em ${formatarDataAtual()}, por ${userName}`, marginLeft, footerY);
          pdf.text(`Página ${i} de ${pageCount}`, pageWidth - marginRight, footerY, { align: 'right' });
        }

        // === Sanitização do nome do arquivo (mesma usada no XLS) ===
        const sanitize = (s) => {
          const base = (s || 'Relatorio')
            .normalize('NFD').replace(/[\u0300-\u036f]/g, '') // remove acentos
            .replace(/[^\w-]+/g, '_')                         // troca não permitidos por _
            .replace(/^_+|_+$/g, '');                         // remove _ nas pontas
          return base || 'Relatorio';
        };
        const pdfFileName = `${sanitize(reportTitle.value)}.pdf`;

        if (generation !== carga) throw new Error('A consulta mudou. Gere o PDF novamente.');
        pdf.save(pdfFileName);
      } catch (err) {
        error.value = err?.response?.data?.error || err.message || 'Falha ao gerar PDF.';
        console.error('Erro ao gerar PDF com autoTable:', err);
      } finally {
        isDownloading.value = false;
      }
    }

    // === XLS ===
    async function gerarXLS() {
      if (busy.value || !valid.value) return;
      error.value = '';
      isDownloadingXls.value = true;
      try {
        // Busca todos os registros do período (todas as páginas) + futuros
        const { all, fut, data: exportData } = await fetchAllExtratoForExport();

        const futEnabledArr = includeFuturos.value ? (fut || []) : [];

        const wb = XLSX.utils.book_new();

        const fBRL = formatCurrency;
        const totPeriodo = totaisResumo(exportData.resumo);
        const totFuturos = includeFuturos.value ? totaisResumo(exportData.futuros_resumo) : { rec: '0.00', desAbs: '0.00', saldo: '0.00' };

        const resumoAOA = [
          ['Relatório',            reportTitle.value || ''],
          ['Subtítulo',            (subtitle.value || '').toString()],
          ['Gerado em',            formatarDataAtual()],
          ['Usuário',              userName || ''],
          ['Registros (Período)',  all.length],
          ['Receitas (Período)',   fBRL(totPeriodo.rec)],
          ['Despesas (Período)',   fBRL(totPeriodo.desAbs)],
          ['Movimento líquido', fBRL(totPeriodo.saldo)],
          ...linhasResumo(exportData.resumo).map(([label, value]) => [label, fBRL(value)]),
          ['Projeção futura', 'Apenas lançamentos exibidos; sem referências futuras'],
          ['Registros Futuros',    futEnabledArr.length],
          ['Receitas Futuros',     fBRL(totFuturos.rec)],
          ['Despesas Futuros',     fBRL(totFuturos.desAbs)],
          ['Saldo Futuros',        fBRL(totFuturos.saldo)],
        ];
        // Size every column from its displayed content; never truncate reference labels.
        const columnWidths = rows => rows[0].map((_, col) => ({
          wch: rows.reduce((width, row) => Math.max(width, String(row[col] ?? '').length), 12) + 2
        }));
        const wsResumo = XLSX.utils.aoa_to_sheet(resumoAOA);
        wsResumo['!cols'] = columnWidths(resumoAOA);
        XLSX.utils.book_append_sheet(wb, wsResumo, '_Resumo');

        const wsRefs = XLSX.utils.aoa_to_sheet([['Data', 'Referência', 'Calculado antes', 'Declarado', 'Diferença de referência'],
          ...linhasReferencias(exportData.referencias_periodo).map(r => [formatDate(r[0]), r[1], ...r.slice(2).map(fBRL)])]);
        wsRefs['!cols'] = columnWidths(XLSX.utils.sheet_to_json(wsRefs, { header: 1 }));
        XLSX.utils.book_append_sheet(wb, wsRefs, 'Referências');

        // Aba principal: Extrato (Período)
        const headers = ['Data','Tipo','Valor','Origem','Autorização','Saldo do dia'];
        const eodPeriodo = mapaSaldos(exportData.saldos_diarios);
        const rows = all.map((l, i, arr) => ([
          formatDate(l.dt_caixa),
          displayTipo(l),
          toNumber(l.valor),
          (l.origem_label || l.origem_tabela || ''),
          (l.numero_autorizacao || ''),
          isFirstOfDay(arr, i) ? (eodPeriodo[dateKey(l.dt_caixa)] == null ? 'Desconhecido' : Number(eodPeriodo[dateKey(l.dt_caixa)])) : null
        ]));
        const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);

        // Larguras de colunas
        ws['!cols'] = columnWidths([headers, ...rows]);

        // Formato numérico com 2 casas decimais na coluna Valor (índice 2)
        if (ws['!ref']) {
          const range = XLSX.utils.decode_range(ws['!ref']);
          for (let R = 1; R <= range.e.r; ++R) {
            // Valor (2)
            let addr = XLSX.utils.encode_cell({ r: R, c: 2 });
            if (ws[addr] && typeof ws[addr].v === 'number') { ws[addr].t = 'n'; ws[addr].z = '#,##0.00'; }
            // Saldo do dia (5)
            addr = XLSX.utils.encode_cell({ r: R, c: 5 });
            if (ws[addr] && typeof ws[addr].v === 'number') { ws[addr].t = 'n'; ws[addr].z = '#,##0.00'; }
          }
        }

        XLSX.utils.book_append_sheet(wb, ws, 'Extrato');

        if (futEnabledArr.length) {
          const headersF = ['Data (venc.)','Tipo','Valor','Origem','Saldo previsto'];
          const eodFut   = mapaSaldos(exportData.futuros_saldos_diarios);
          const rowsF    = futEnabledArr.map((l, i, arr) => ([
            formatDate(l.dt_caixa),
            displayTipo(l),
            toNumber(l.valor),
            (l.origem_label || l.origem_tabela || ''),
            isFirstOfDay(arr, i) ? (eodFut[dateKey(l.dt_caixa)] == null ? 'Desconhecido' : Number(eodFut[dateKey(l.dt_caixa)])) : null
          ]));
          const wsF = XLSX.utils.aoa_to_sheet([headersF, ...rowsF]);
          wsF['!cols'] = columnWidths([headersF, ...rowsF]);
          if (wsF['!ref']) {
            const rangeF = XLSX.utils.decode_range(wsF['!ref']);
            for (let R = 1; R <= rangeF.e.r; ++R) {
              let addr = XLSX.utils.encode_cell({ r: R, c: 2 }); // Valor
              if (wsF[addr] && typeof wsF[addr].v === 'number') { wsF[addr].t = 'n'; wsF[addr].z = '#,##0.00'; }
              addr = XLSX.utils.encode_cell({ r: R, c: 4 });     // Saldo previsto
              if (wsF[addr] && typeof wsF[addr].v === 'number') { wsF[addr].t = 'n'; wsF[addr].z = '#,##0.00'; }
            }
          }
          XLSX.utils.book_append_sheet(wb, wsF, 'Lançamentos Futuros');
        }

        const base = (reportTitle.value || 'Relatorio')
          .normalize('NFD').replace(/[\u0300-\u036f]/g, '') // remove acentos
          .replace(/[^\w-]+/g, '_')                         // troca não permitidos por _
          .replace(/^_+|_+$/g, '');                         // tira _ das pontas
        const fileName = `${base || 'Relatorio'}.xls`;

        XLSX.writeFile(wb, fileName, { bookType: 'xls' });
      } catch (err) {
        error.value = err?.response?.data?.error || err.message || 'Falha ao gerar Excel.';
        console.error('Erro ao gerar Excel:', err);
      } finally {
        isDownloadingXls.value = false;
      }
    }

    onMounted(() => {
      carregar();
      window.addEventListener('keydown', printShortcut);
      window.addEventListener('afterprint', terminarImpressao);
    });
    onBeforeUnmount(() => {
      ++carga;
      window.removeEventListener('keydown', printShortcut);
      window.removeEventListener('afterprint', terminarImpressao);
    });
    watch(() => props.contaId, () => { active = null; carregar(); });

    return {
      hoje, loading, error, notice, valid, busy, printDocument, printReady, itensVisiveis, imprimir, changeLimit, pageOptions, startRow, endRow,
      snapshot, periodo, resumo, itens,
      paginacao, page, pageSize, totalPages,
      filters,
      reportTitle, subtitle, userName, logoPath,
      formatCurrency, formatDate, formatarDataAtual,
      carregar, aplicarFiltros, resetarFiltros, mudarPagina,
      gerarPDF, gerarXLS,
      isDownloading, isDownloadingXls,
      printArea,
      emitClose, totaisPeriodo, displayTitle,
      logoUrl, futuros, totaisFuturos,
      dateKey, isLastOfDay, isFirstOfDay,
      displayTipo, includeFuturos,
      eodPeriodoMapFull, eodFuturosMapFull, referenciasLinhas, resumoLinhas,
    };
  }
};
</script>

<style scoped>
.print-warning { display: none; }
/* ---- Modal ---- */
.modal-overlay {
  position: fixed; inset: 0;
  background: rgba(0,0,0,.5);
  z-index: 9999; display: flex; align-items: center; justify-content: center;
}
.modal-container {
  background: #fff; width: 100%; max-width: 1100px; border-radius: 6px; overflow: hidden;
  max-height: 90vh; display: flex; flex-direction: column;
}
.modal-container.modal-xl { max-width: 1200px; }
.modal-header {
  background: #f3f3f3; padding: 10px 14px; display: flex; justify-content: space-between; align-items: center;
}
.close-icon { font-size: 1.2rem; cursor: pointer; }
.print-area { overflow-y: auto; overflow-x: hidden; min-width: 0; max-height: calc(90vh - 48px); }
.modal-container, .extrato-content, .relatorio-body { min-width: 0; }
.table-responsive { max-width: 100%; }
.reference-table { min-width: 620px; }
.reference-table th:nth-child(n+3), .reference-table td:nth-child(n+3) { text-align: right; }
.reference-table td, .balance-summary td { white-space: nowrap; font-variant-numeric: tabular-nums; }
.balance-context > div { flex: 1 1 240px; }
.filters-row > div { min-width: 0; max-width: 100%; }
.right-controls .form-check { flex: 1 0 100%; }
.right-controls .btn { flex-shrink: 0; margin-right: 0 !important; }
@media (max-width: 575px) {
  .balance-context .text-end { text-align: left !important; }
  .modal-header h6 { min-width: 0; margin-right: 12px; }
  .close-icon { flex-shrink: 0; }
  .conciliacao-legenda { flex-wrap: wrap; justify-content: flex-end; }
}

/* ---- Área imprimível estilo Relatório ---- */
.relatorio-header {
  display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px;
}
.header-logo { height: 48px; }
.relatorio-title {
  text-align: center; font-size: 18px; font-weight: bold; margin-bottom: 8px; color: #222;
}
.relatorio-subtitle {
  text-align: center; font-size: 12px; color: #555; margin-bottom: 14px;
}
.relatorio-body { margin-top: 4px; }
.relatorio-body h6 { font-size: 14px; margin-top: 10px; margin-bottom: 6px; }
.relatorio-footer {
  margin-top: 12px; text-align: right; font-size: 12px; font-style: italic; color: #333;
}

.filters-row .form-label {
  font-weight: 700;   /* igual ao fw-bold */
  font-size: 0.7rem;    /* mesmo tamanho do texto padrão */
  color: #212529;     /* mesmo tom do texto base */
}

/* ---- Botões no estilo dos relatórios ---- */
.btn-pdf, .btn-xls, .btn-apl, .btn-lim { font-size: 12px; padding: 4px 8px; height: 30px; border: none;}

/* Checkbox dos filtros com a mesma tipografia dos rótulos "De/Até" */
.filters-row .form-check-label {
  font-weight: 700;
  font-size: 0.7rem;
  color: #212529;
  margin: 0;
  white-space: normal;
  line-height: 1;      /* alinhamento mais firme com o input */
}

.filters-row .form-check-input {
  margin-top: 0; /* remove deslocamento vertical padrão do bootstrap */
  width: 1rem;
  height: 1rem;
}

.conciliacao-status-dot {
  display: inline-block;
  width: 7px;
  height: 7px;
  margin-right: 6px;
  border-radius: 50%;
  vertical-align: 1px;
  border: 1px solid rgba(0, 0, 0, 0.08);
}

.conciliacao-status-dot--conciliado {
  background-color: #198754;
}

.conciliacao-status-dot--pendente {
  background-color: #adb5bd;
}

.conciliacao-legenda {
  display: flex;
  gap: 12px;
  font-size: 0.68rem;
  color: #6c757d;
}

.conciliacao-legenda span {
  display: inline-flex;
  align-items: center;
}

.conciliacao-legenda .conciliacao-status-dot {
  margin-right: 4px;
}

.col-data {
  width: 88px;
  min-width: 88px;
  white-space: nowrap;
}

.col-origem {
  width: 48%;
  max-width: 520px;
}

.origem-texto {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Print: esconder controles e cabeçalhos do modal */
@media print {
  .print-area:not(.print-ready) .extrato-content { display: none !important; }
  .print-area:not(.print-ready) .print-warning { display: block; }
  .modal-overlay { position: static; background: transparent; }
  .modal-container { max-width: 100%; box-shadow: none; max-height: initial; }
  .no-print, .nao-imprimir { display: none !important; }
  .print-area { overflow: visible; max-height: none; }
  .modal-container { display: block; overflow: visible; }
  .table-responsive { overflow: visible; }
  .reference-table { min-width: 0; }
  .origem-texto { white-space: normal; overflow: visible; }
  thead { display: table-header-group; }
  tr { break-inside: avoid; }
  body { background: #fff; }
}
</style>
